import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { nok, formatDate } from "@/lib/format";

const compact = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(n % 1e6 === 0 ? 0 : 1)} M`;
  if (Math.abs(n) >= 1e3) return `${Math.round(n / 1e3)} k`;
  return String(n);
};

export function ValuationHistoryChart({ companyId, refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/companies/${companyId}/valuation-history`).then((r) => setData(r.data)).catch(() => setData({ points: [] }));
  }, [companyId, refreshKey]);

  const points = (data?.points || []).map((p, i) => ({ ...p, idx: i, dateLabel: formatDate(p.date) }));
  const headerVal = data ? (data.current || (points.length ? points[points.length - 1].valuation : 0)) : 0;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-6 shadow-sm" data-testid="valuation-history-card">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">
          <TrendingUp className="h-4 w-4 text-primary" /> Verdiutvikling
        </h3>
        {data ? <span className="text-sm font-semibold" data-testid="valuation-history-current">{nok(headerVal)}</span> : null}
      </div>

      {data === null ? (
        <Skeleton className="mt-4 h-44 rounded-lg" />
      ) : points.length === 0 ? (
        <div className="mt-4 grid h-44 place-items-center rounded-lg border border-dashed border-border text-center text-xs text-muted-foreground" data-testid="valuation-history-empty">
          <p className="max-w-[240px]">Verdiutviklingen vises her etter hvert som prisede aksjetransaksjoner registreres.</p>
        </div>
      ) : (
        <div className="mt-3 h-44">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={150}>
            <AreaChart data={points} margin={{ top: 6, right: 6, left: -6, bottom: 0 }}>
              <defs>
                <linearGradient id="valFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="dateLabel" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} minTickGap={20} />
              <YAxis tickFormatter={compact} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickLine={false} axisLine={false} width={44} />
              <Tooltip
                formatter={(v, _n, p) => [`${nok(v)}${p?.payload?.price_per_share ? ` · ${nok(p.payload.price_per_share)}/aksje` : ""}`, "Verdsettelse"]}
                labelFormatter={(l, pl) => `${l}${pl && pl[0]?.payload?.type ? ` · ${pl[0].payload.type}` : ""}`}
                contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))", fontSize: 12 }}
              />
              <Area type="monotone" dataKey="valuation" stroke="hsl(var(--chart-1))" strokeWidth={2.5} fill="url(#valFill)" dot={{ r: 3, fill: "hsl(var(--chart-1))" }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
